import i18n from 'i18next';
import { TABLE_CELL_STATUS, TABLE_CELL_TYPE, VIEW_MODE } from 'types/enums.ts';
import type { CellContext, TableOptions } from '@tanstack/react-table';
import { type ReactNode } from 'react';
import type { ControllerRenderProps, FieldError } from 'react-hook-form';
import { type Control, Controller, type FieldValues, type Path } from 'react-hook-form';
import type { CSSProperties } from 'react';
import type { Column } from '@tanstack/react-table';

import { Button } from 'components/common';
import { sanitizeRichText } from 'utils/quill';
import { HeliosRichTextTableFunctions } from './HeliosRichText.styles';
import { DateComponent } from './cellComponents/Date';
import { DateRange } from './cellComponents/DateRange';
import { Icon } from './cellComponents/Icon';
import { NameArray } from './cellComponents/NameArray';
import { Select } from './cellComponents/Select';
import { Status } from './cellComponents/Status';
import { Switch } from './cellComponents/Switch';
import { Text } from './cellComponents/Text';
import { Username } from './cellComponents/Username';
import { AdminIconsArray } from './cellComponents/AdminIconsArray';
import { EMAIL_REGEX, GUID_REGEX, PHONE_REGEX } from 'utils/regex';
import { Checkbox } from 'components/mui/common/Checkbox';
import { File } from './cellComponents/File';
import { TextField } from 'components/mui/common/TextField';
import type { Option } from 'components/mui/common/Autocomplete';
import { Autocomplete } from 'components/mui/common/Autocomplete';
import { FilterDynamicDateInput } from '../Inputs/FilterDynamicDateInput';
import { FilterNumberInput } from '../Inputs/FilterNumberInput';
import { ValueHelios } from './cellComponents/helios/Value';
import { StaticSelectionHelios } from './cellComponents/helios/StaticSelection';
import { FileHelios } from './cellComponents/helios/File';
import { LinkHelios } from './cellComponents/helios/Link';
import { BrowseHelios } from './cellComponents/helios/Browse';
import { ButtonHelios } from './cellComponents/helios/Button';
import type { HeliosRecordDataTableColumnFormatting, HeliosRecordDataTableColumnSettings } from 'types/dataHelios';
import type { HeliosCellData } from 'types/helios/agendaListing';
import type { ColumnType, DataSchema, FormItemDefinition, GetSimpleUserListRes, Schema, ValueTypeByTableCellType } from 'types';
import { convertValueByType, NEW_RECORD_PREFIX } from 'utils/agendaRecord/agendaRecord';
import { validateNameArray } from 'utils/validations/nameArrayValidation';
import type { ColumnOverflowMode, FormItemUiDefinition, UserItem } from 'types/formItem';
import type { ContextMenuItem } from 'utils/hooks/userAgendaTable/useAgendaContextMenu';
import { UsersFilter } from 'components/mui/common/Drawer/Containers/Filter/UsersFilter';
import { RecordsFilter } from 'components/mui/common/Drawer/Containers/Filter/RecordsFilter';
import { RecordsFilterAutocomplete } from 'components/mui/common/Drawer/Containers/Filter/RecordsFilterAutocomplete';
import type { TableFormData } from './Table.tsx';
import { getUUID } from 'utils/common.ts';
import { isOdd } from 'utils/math/math.ts';
import { TextArea } from 'components/mui/common/TextArea';

export const getComponentByType = <T, P>(
  type: TABLE_CELL_TYPE,
  originalType: string | undefined,
  value: ValueTypeByTableCellType[TABLE_CELL_TYPE],
  row: CellContext<T, P>,
  onClick?: (row: TableOptions<T>['data'][number], cell: keyof P, newRow?: TableOptions<T>['data'][number]) => unknown,
  clickableCellsKeys?: (keyof P)[] | 'all',
  values?: string[],
  // HELIOS
  dataType?: string,
  settings?: HeliosRecordDataTableColumnSettings,
  formatting?: HeliosRecordDataTableColumnFormatting,
  templateItemId?: number,
  templateViewId?: string,
  cellData?: HeliosCellData,
  formItemUiDefinition?: FormItemUiDefinition,
  formItemDefinition?: FormItemDefinition,
  isNestedList?: boolean,
  contextMenuItems?: ContextMenuItem[]
): ReactNode => {
  const t = i18n.t;
  let isClickable = false;
  const columnWidth = `calc(var(--col-${CSS.escape(row.column.id)}-size) * 1px - 15px)`;
  const overflowMode = (row.column.columnDef.meta as { columnOverflowMode?: ColumnOverflowMode })?.columnOverflowMode;

  if (clickableCellsKeys === 'all') {
    isClickable = true;
  } else if (clickableCellsKeys) {
    isClickable = clickableCellsKeys.includes(row.column.id as keyof P);
  }

  switch (type) {
    case TABLE_CELL_TYPE.ID:
      return;
    case TABLE_CELL_TYPE.TEXT:
      if (formatting?.formatValue?.typeSpecific?.formatText?.showAsRichText) {
        return (
          <HeliosRichTextTableFunctions>
            <div className="ql-editor" dangerouslySetInnerHTML={{ __html: sanitizeRichText((value as string) ?? '') }} />
          </HeliosRichTextTableFunctions>
        );
      }
      if (formatting?.formatValue?.typeSpecific?.formatText?.showAsTextArea) {
        return (
          <TextArea
            value={(value as string) ?? ''}
            readOnly
            fullWidth
            tabIndex={-1}
            maxRows={3}
            minHeight={null}
            style={{ display: 'flex', justifySelf: 'center', alignSelf: 'center' }}
          />
        );
      }

      return (
        <Text
          type={type}
          originalType={originalType}
          value={value as string}
          row={row}
          onClick={onClick}
          isClickable={isClickable}
          formItemUiDefinition={formItemUiDefinition}
          formItemDefinition={formItemDefinition}
        />
      );
    case TABLE_CELL_TYPE.NUMBER:
    case TABLE_CELL_TYPE.PHONE_NUMBER:
    case TABLE_CELL_TYPE.EMAIL:
    case TABLE_CELL_TYPE.EMAIL_WITH_USER_ID:
    case TABLE_CELL_TYPE.GUID:
      return (
        <Text
          type={type}
          originalType={originalType}
          value={value as string}
          row={row}
          onClick={onClick}
          isClickable={isClickable}
          formItemUiDefinition={formItemUiDefinition}
          formItemDefinition={formItemDefinition}
        />
      );
    case TABLE_CELL_TYPE.DATE:
      return (
        <DateComponent value={value as string} row={row} onClick={onClick} isClickable={isClickable} formItemUiDefinition={formItemUiDefinition} />
      );
    case TABLE_CELL_TYPE.MULTI_SELECT:
      return <Status value={value as TABLE_CELL_STATUS} row={row} onClick={onClick} isClickable={isClickable} />;
    case TABLE_CELL_TYPE.STATUS:
      return <Status value={value as TABLE_CELL_STATUS} row={row} onClick={onClick} isClickable={isClickable} />;
    case TABLE_CELL_TYPE.USERNAME:
      return <Username value={value as string} row={row} onClick={onClick} isClickable={isClickable} />;
    case TABLE_CELL_TYPE.USERS:
      return (
        <NameArray
          maxWidth={columnWidth}
          overflowMode={overflowMode}
          value={((value ?? []) as GetSimpleUserListRes['items']).map(({ fullName }: { fullName: string }) => fullName)}
          formItemUiDefinition={formItemUiDefinition}
        />
      );
    case TABLE_CELL_TYPE.SWITCH:
      return <Switch checked={value as boolean} onClick={onClick} row={row} isClickable={isClickable} />;
    case TABLE_CELL_TYPE.NAME_ARRAY:
      return (
        <NameArray maxWidth={columnWidth} overflowMode={overflowMode} value={validateNameArray(value)} formItemUiDefinition={formItemUiDefinition} />
      );
    case TABLE_CELL_TYPE.ADMIN_ICONS_ARRAY:
      return <AdminIconsArray value={value as string[]} />;
    case TABLE_CELL_TYPE.DATE_RANGE:
      return <DateRange value={value as [Date, Date | null]} row={row} onClick={onClick} />;
    case TABLE_CELL_TYPE.ICON:
      return <Icon value={value as string} row={row} onClick={onClick} isClickable={isClickable} />;

    case TABLE_CELL_TYPE.BUTTON:
      return (
        <Button
          className=" max-w-20"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            if (onClick) {
              onClick(row.row.original, row.column.id as keyof P);
            }
          }}>
          {t(`common.${row.column.id}`)}
        </Button>
      );

    case TABLE_CELL_TYPE.SELECT:
      return <Select value={value as string} row={row} onClick={onClick} isClickable={isClickable} values={values} />;

    case TABLE_CELL_TYPE.LINK:
      return (
        <a href={value as string} target="_blank" rel="noreferrer noopener" className="text-blue-400 underline transition-all cursor-pointer">
          {value as string}
        </a>
      );

    case TABLE_CELL_TYPE.COMMENT:
      const commentsLength = (value as string[])?.length || 0;
      return <Text type={type} value={`${t('common.count')}: ${commentsLength}`} row={row} formItemUiDefinition={formItemUiDefinition} />;
    case TABLE_CELL_TYPE.FILE_UPLOAD:
      return <File value={value as string[]} formItemUiDefinition={formItemUiDefinition} />;

    case TABLE_CELL_TYPE.CHECKBOX:
      return <Checkbox checked={Boolean(value)} disabled formItemUiDefinition={formItemUiDefinition} />;
    case TABLE_CELL_TYPE.NODE:
      if (!isClickable || !onClick) {
        return value as ReactNode;
      }

      return (
        <span
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick(row.row.original, row.column.id as keyof P);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onClick(row.row.original, row.column.id as keyof P);
            }
          }}>
          {value as ReactNode}
        </span>
      );

    //HELIOS
    case TABLE_CELL_TYPE.HELIOS_VALUE:
      return (
        <ValueHelios
          value={value as string}
          row={row}
          onClick={onClick}
          isClickable={isClickable}
          dataType={dataType}
          data={cellData}
          settings={settings}
          formItemUiDefinition={formItemUiDefinition}
        />
      );
    case TABLE_CELL_TYPE.HELIOS_STATIC_SELECTION:
      return (
        <StaticSelectionHelios
          value={value as string}
          row={row}
          onClick={onClick}
          isClickable={isClickable}
          dataType={dataType}
          staticSelection={settings?.staticSelection}
          formItemUiDefinition={formItemUiDefinition}
        />
      );
    case TABLE_CELL_TYPE.HELIOS_BROWSE:
      return (
        <BrowseHelios value={value as string} templateItemId={templateItemId} templateViewId={templateViewId} data={cellData} settings={settings} />
      );
    case TABLE_CELL_TYPE.HELIOS_BUTTON:
      if (!isNestedList || !contextMenuItems?.length) return null;

      return <ButtonHelios row={row} contextMenuItems={contextMenuItems} />;
    case TABLE_CELL_TYPE.HELIOS_FILE:
      return (
        <FileHelios value={value as string} templateItemId={templateItemId} templateViewId={templateViewId} data={cellData} settings={settings} />
      );
    case TABLE_CELL_TYPE.HELIOS_LINK:
      return <LinkHelios value={value as string} dataType={dataType} settings={settings} />;

    default:
      return null;
  }
};

export const allowedTypesToFilter = [
  TABLE_CELL_TYPE.TEXT,
  TABLE_CELL_TYPE.DATE,
  TABLE_CELL_TYPE.EMAIL,
  TABLE_CELL_TYPE.PHONE_NUMBER,
  TABLE_CELL_TYPE.GUID,
  TABLE_CELL_TYPE.STATUS,
  TABLE_CELL_TYPE.NAME_ARRAY,
  TABLE_CELL_TYPE.USERS,
  TABLE_CELL_TYPE.CHECKBOX,
];

export const disallowedOriginalTypesToFilter: string[] = [];

export interface TableCell {
  columnId: string;
  text?: { value: string };
  email?: { value: string };
  phoneNumber?: { value: string };
  number?: { value: string };
  reference?: { value: string };
  rowId: string;
}

export const getComponentByTypeForFilters = <K extends FieldValues>({
  originalType,
  type,
  registerName,
  control,
  label,
  dateMask,
  options,
  isDisabled,
  allowMultiple = true,
  statusOptions,
  radioButtonOptions,
  formItemDefinition,
  formItemId,
}: {
  originalType: string;
  type: TABLE_CELL_TYPE;
  control: Control<K>;
  registerName: Path<K>;
  label: string;
  dateMask?: string;
  options?: string[];
  isDisabled?: boolean;
  allowMultiple?: boolean;
  statusOptions?: Option[];
  radioButtonOptions?: Option[];
  formItemDefinition?: FormItemDefinition;
  formItemId: string;
}) => {
  /*
   * TODO: This is not a nice handling, considering the admin gets no feedback
   * when setting the fields up as filters. Consider improving and maybe somehow
   * logging this to the console or showing a warning in the UI in the future.
   */
  const viewMode = formItemDefinition?.records?.viewMode || formItemDefinition?.Records?.viewMode;

  if (!allowedTypesToFilter.includes(type)) return;
  if (disallowedOriginalTypesToFilter.includes(originalType)) return;
  if (viewMode === VIEW_MODE.NESTED_MAPPING) return;

  const t = i18n.t;

  const rulesPattern = () => {
    switch (type) {
      case TABLE_CELL_TYPE.EMAIL:
        return {
          value: EMAIL_REGEX,
          message: t('common.invalidEmail'),
        };
      case TABLE_CELL_TYPE.PHONE_NUMBER:
        return {
          value: PHONE_REGEX,
          message: t('common.invalidPhoneNumber'),
        };
      case TABLE_CELL_TYPE.GUID:
        return {
          value: GUID_REGEX,
          message: t('common.invalidGuid'),
        };
      default:
        return;
    }
  };

  const getField = (field: ControllerRenderProps<K, Path<K>>, error: FieldError | undefined) => {
    switch (type) {
      case TABLE_CELL_TYPE.TEXT:
      case TABLE_CELL_TYPE.EMAIL:
      case TABLE_CELL_TYPE.PHONE_NUMBER:
      case TABLE_CELL_TYPE.GUID:
        if (originalType === 'Number') {
          return (
            <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
              <FilterNumberInput
                disabled={isDisabled}
                label={`${t('common.from')} - ${label}`}
                layout="half"
                onChange={(value) => {
                  const currentValue = field.value || {};
                  field.onChange({
                    ...currentValue,
                    fromNumber: value,
                  });
                }}
                value={field.value?.fromNumber || ''}
                error={Boolean(error)}
              />
              <FilterNumberInput
                disabled={isDisabled}
                label={`${t('common.to')} - ${label}`}
                layout="half"
                onChange={(value) => {
                  const currentValue = field.value || {};
                  field.onChange({
                    ...currentValue,
                    toNumber: value,
                  });
                }}
                value={field.value?.toNumber || ''}
                error={Boolean(error)}
              />
            </div>
          );
        }
        if (originalType === 'RadioButton') {
          return (
            <Autocomplete
              {...field}
              disabled={isDisabled}
              background="white"
              label={label}
              onChange={(value) => {
                if (!value) return field.onChange(null);
                const typedValue = value as { label: string; value: string };
                field.onChange(typedValue.value);
              }}
              options={radioButtonOptions ?? []}
              value={field.value}
              error={Boolean(error)}
            />
          );
        }
        return (
          <TextField
            disabled={isDisabled}
            label={label}
            {...field}
            type={type}
            error={Boolean(error)}
            disableHelperText={!error}
            helperText={error?.message}
            fullWidth
          />
        );
      case TABLE_CELL_TYPE.DATE:
        return (
          <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
            <FilterDynamicDateInput
              disabled={isDisabled}
              label={`${t('common.from')} - ${label}`}
              layout="half"
              onChange={(date) => {
                const currentValue = field.value || {};
                field.onChange({
                  ...currentValue,
                  fromDate: date,
                });
              }}
              value={field.value?.fromDate || ''}
              error={Boolean(error)}
              dateMask={{
                Day: dateMask?.includes('Day') ?? true,
                Month: dateMask?.includes('Month') ?? true,
                Year: dateMask?.includes('Year') ?? true,
                Time: dateMask?.includes('Time') ?? false,
              }}
            />
            <FilterDynamicDateInput
              disabled={isDisabled}
              label={`${t('common.to')} - ${label}`}
              layout="half"
              onChange={(date) => {
                const currentValue = field.value || {};
                field.onChange({
                  ...currentValue,
                  toDate: date,
                });
              }}
              value={field.value?.toDate || ''}
              error={Boolean(error)}
              dateMask={{
                Day: dateMask?.includes('Day') ?? true,
                Month: dateMask?.includes('Month') ?? true,
                Year: dateMask?.includes('Year') ?? true,
                Time: dateMask?.includes('Time') ?? false,
              }}
            />
          </div>
        );
      case TABLE_CELL_TYPE.STATUS:
        return (
          <Autocomplete
            {...field}
            background="white"
            label={label}
            disabled={isDisabled}
            options={
              statusOptions ?? [
                { label: t('table.status.active'), value: TABLE_CELL_STATUS.ACTIVE },
                { label: t('table.status.archived'), value: TABLE_CELL_STATUS.ARCHIVED },
                { label: t('table.status.draft'), value: TABLE_CELL_STATUS.DRAFT },
              ]
            }
            onChange={(value) => {
              if (!value) return field.onChange(null);
              const typedValue = value as { label: string; value: string };
              field.onChange(typedValue.value);
            }}
            error={Boolean(error)}
            disableHelperText
          />
        );
      case TABLE_CELL_TYPE.USERS:
        return <UsersFilter {...field} disabled={isDisabled} label={label} roleIds={formItemDefinition?.users?.roleIds ?? []} />;
      case TABLE_CELL_TYPE.NAME_ARRAY:
        if (viewMode === VIEW_MODE.DIALOG) {
          return (
            <RecordsFilter
              {...field}
              disabled={isDisabled}
              label={label}
              recordDefinition={formItemDefinition?.records || formItemDefinition?.Records}
            />
          );
        } else {
          return (
            <RecordsFilterAutocomplete
              formItemId={formItemId}
              label={label}
              multiple={allowMultiple}
              value={field.value}
              onChange={(value) => {
                const typedValue = value as Option | Option[];
                if (Array.isArray(typedValue)) return field.onChange(typedValue?.length === 0 ? null : typedValue);
                field.onChange(typedValue?.value);
              }}
              error={Boolean(error)}
              disabled={isDisabled}
              tablesDefinition={formItemDefinition?.tables}
              options={options}
            />
          );
        }
      case TABLE_CELL_TYPE.CHECKBOX: {
        return (
          <Autocomplete
            {...field}
            disabled={isDisabled}
            background="white"
            label={label}
            options={[
              { label: t('common.yes'), value: 'true' },
              { label: t('common.no'), value: 'false' },
            ]}
            onChange={(value) => {
              if (!value) return field.onChange(null);
              const typedValue = value as Option;
              field.onChange(typedValue.value);
            }}
            error={Boolean(error)}
            disableHelperText
          />
        );
      }
      default:
        return <TextField disabled={isDisabled} label={label} {...field} type="text" error={Boolean(error)} helperText={error?.message} fullWidth />;
    }
  };

  return (
    <Controller
      control={control}
      name={registerName}
      rules={{
        pattern: rulesPattern(),
      }}
      render={({ field, fieldState: { error } }) => getField(field, error)}
    />
  );
};

export type TableRow<T extends keyof P, P extends DataSchema> = Schema<T, P> & { id: string; identifier: string };

export const createEmptyRow = <T extends keyof P, P extends DataSchema>(dataSchema: P): TableRow<T, P> => {
  const emptyRow = {} as TableRow<T, P>;

  (Object.keys(dataSchema) as T[]).forEach((key) => {
    const entry = dataSchema[key];
    let value: string | Date | boolean | ReactNode | string[] | [Date | null, Date | null] | TABLE_CELL_STATUS | undefined | null;

    switch (entry.type) {
      case TABLE_CELL_TYPE.TEXT:
      case TABLE_CELL_TYPE.USERNAME:
      case TABLE_CELL_TYPE.EMAIL:
      case TABLE_CELL_TYPE.ID:
      case TABLE_CELL_TYPE.ICON:
        value = '';
        break;
      case TABLE_CELL_TYPE.DATE:
        value = null;
        break;
      case TABLE_CELL_TYPE.STATUS:
        value = [TABLE_CELL_STATUS.ACTIVE];
        break;
      case TABLE_CELL_TYPE.SWITCH:
        value = false;
        break;
      case TABLE_CELL_TYPE.BUTTON:
        value = <div />;
        break;
      case TABLE_CELL_TYPE.USERS:
      case TABLE_CELL_TYPE.NAME_ARRAY:
        value = [];
        break;
      case TABLE_CELL_TYPE.DATE_RANGE:
        value = [null, null];
        break;
      case TABLE_CELL_TYPE.NUMBER:
        value = '';
        break;
      case TABLE_CELL_TYPE.CHECKBOX:
        value = false;
        break;
      default:
        value = undefined;
    }

    emptyRow[key] = value as Schema<T, P>[T];
  });

  const index = getUUID();

  emptyRow.identifier = `new-${index}` as Schema<T, P>['identifier'];
  emptyRow.id = `new-${index}` as Schema<T, P>['id'];

  return emptyRow satisfies TableRow<T, P>;
};

export const getEnumByColumnType = (type: ColumnType['type']) => {
  switch (type) {
    case 'Number':
      return TABLE_CELL_TYPE.NUMBER;

    case 'Email':
      return TABLE_CELL_TYPE.EMAIL;

    case 'EmailWithUserId':
      return TABLE_CELL_TYPE.EMAIL_WITH_USER_ID;

    case 'PhoneNumber':
      return TABLE_CELL_TYPE.PHONE_NUMBER;

    case 'Reference':
      return TABLE_CELL_TYPE.GUID;

    case 'Text':
    default:
      return TABLE_CELL_TYPE.TEXT;
  }
};

export const processCellsValuesFromData = <T extends keyof P, P extends DataSchema>(dataSchema: P, dataRow: Schema<T, P>) => {
  return (Object.keys(dataSchema) as T[]).reduce<Record<string, unknown>>((acc, key) => {
    if (key === 'id') return acc;

    const schemaItem = dataSchema[key];
    if (!schemaItem) return acc;

    const { type } = schemaItem;
    const cellValue = getCellFormStateValue(type, dataRow[key]);

    acc[key as string] = convertValueByType(cellValue, type);
    return acc;
  }, {});
};

export const getCellFormStateValue = (type: TABLE_CELL_TYPE, cellValue: unknown): unknown => {
  if (type === 'date' && Array.isArray(cellValue) && cellValue.length > 1) {
    return cellValue[0];
  }

  if (type === 'users' && Array.isArray(cellValue)) {
    return cellValue.map((user: UserItem) => user.userId);
  }

  if (type === TABLE_CELL_TYPE.HELIOS_BROWSE) {
    return hasRawValue(cellValue) ? cellValue.rawValue : cellValue;
  }

  if (hasRawValue(cellValue)) {
    if (hasRawValueProps<{ id: string }>(cellValue, 'id')) {
      return cellValue.rawValue.id;
    } else {
      return cellValue.rawValue;
    }
  }

  return cellValue;
};

export const hasRawValue = (cellValue: unknown): cellValue is { rawValue: unknown } => {
  return !!(cellValue && typeof cellValue === 'object' && 'rawValue' in cellValue);
};

export const hasRawValueProps = <T,>(cellValue: { rawValue: unknown }, props: string | string[]): cellValue is { rawValue: T } => {
  if (cellValue === null || cellValue.rawValue === null || typeof cellValue.rawValue !== 'object') {
    return false;
  }

  const rawValueObj = cellValue.rawValue;

  if (Array.isArray(props)) {
    return props.every((prop) => prop in rawValueObj);
  }

  return props in rawValueObj;
};

export const processDataToFormData = <T extends keyof P, P extends DataSchema>(
  dataD: Schema<T, P>[],
  formData: TableFormData,
  dataSchema: P
): TableFormData => {
  return dataD.reduce<TableFormData>(
    (acc, dataRow) => {
      const rowId = dataRow.identifier;
      const cells = processCellsValuesFromData(dataSchema, dataRow);

      Object.entries(cells).forEach(([cellKey, cellValue]) => {
        if (!acc[rowId]) {
          acc[rowId] = {};
        }

        if (acc[rowId][cellKey] === undefined) {
          acc[rowId][cellKey] = cellValue;
        }
      });

      return acc;
    },
    { ...dropRemovedNewRows(dataD, formData) }
  );
};

const dropRemovedNewRows = <T extends keyof P, P extends DataSchema>(dataD: Schema<T, P>[], formData: TableFormData): TableFormData => {
  const identifiers = new Set(dataD.map((row) => row.identifier));

  return Object.entries(formData).reduce<TableFormData>((acc, [rowId, rowData]) => {
    const isAddedRow = rowId.startsWith(NEW_RECORD_PREFIX);
    if (!isAddedRow || identifiers.has(rowId)) {
      acc[rowId] = rowData;
    }
    return acc;
  }, {});
};

export const getRowId = (rowId: string): string => {
  const match = rowId.match(/^i__\d+__(.+)$/);
  return match ? match[1] : rowId;
};

export const getCommonPinningStyles = <TData,>(
  column: Column<TData>,
  { type, id }: { type?: 'header' | 'cell'; id?: string } = {}
): CSSProperties => {
  const isHeader = type === 'header';

  const isPinned = column.getIsPinned();
  const isPinnedLeft = isPinned === 'left';
  const isPinnedRight = isPinned === 'right';

  const isLastLeft = isPinnedLeft && column.getIsLastColumn('left');
  const isFirstRight = isPinnedRight && column.getIsFirstColumn('right');

  const isOddCellId = id && isOdd(id);
  const backgroundColorOddCell = isOddCellId ? '#ededed' : 'white';
  const backgroundColor = isHeader ? 'primary.bg_primary' : backgroundColorOddCell;
  const boxShadowLeft = isLastLeft ? '-4px 0 4px -4px gray inset' : undefined;
  const boxShadowRight = isFirstRight ? '4px 0 4px -4px gray inset' : undefined;
  const exactSizing = (column.columnDef.meta as { exactSizing?: boolean })?.exactSizing;
  const pinnedOffset = (side: 'left' | 'right', width: number) =>
    exactSizing ? `calc(var(--col-${CSS.escape(column.id)}-${side}, 0) * 1px)` : `${width}px`;

  return {
    ...((column.columnDef.meta as { columnOverflowMode?: ColumnOverflowMode })?.columnOverflowMode ? { paddingLeft: 0, paddingRight: 0 } : {}),
    boxShadow: boxShadowLeft || boxShadowRight,
    left: isPinnedLeft ? pinnedOffset('left', column.getStart('left')) : undefined,
    right: isPinnedRight ? pinnedOffset('right', column.getAfter('right')) : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: isPinned ? (exactSizing ? `calc(var(--col-${CSS.escape(column.id)}-size) * 1px)` : column.getSize()) : undefined,
    zIndex: isPinned ? 1 : undefined,
    backgroundColor: isPinned ? backgroundColor : undefined,
  };
};
